import { useRecoilCallback, type Snapshot } from 'recoil'
import { useTranslation } from 'react-i18next'
import { LOTUS_FUNERAL_HOME_NOT_ENABLED_CODE } from '@messenger/consts'
import { v4 as uuidv4 } from 'uuid'
import type { EditorTextBoxInput } from '@messenger/lotus/row-store'
import {
  getEditorPageContent,
  saveEditorPageContent,
} from '@/api/editorPageContent'
import {
  editorPageContentAtomFamily,
  editorTextBoxSeedIdsAtom,
} from '@/atoms/editorTextBoxAtoms'
import {
  baseLayoutMarginsSelector,
  canvasPageIdAtom,
  editorPagesAtom,
  selectedItemsAtom,
  unsavedChangesAtom,
} from '@/atoms/_editorAtoms'
import { selectedPageAtom } from '@/atoms/editorAtoms'
import { useSyncCurrentPageToEditorPages } from '@/hooks/editor/useSyncCurrentPageToEditorPages'
import { useErrorHandler } from '@/hooks/utility/useErrorHandler'
import {
  EDITOR_TEXT_BOX_SEED_HEIGHT_PX,
  EDITOR_TEXT_BOX_SEED_TEXT,
} from '@/utils/editor-text-box/editorTextBoxAdapter'
import zLayers from '@/utils/styling/zLayers'
import { logger, EventCategory } from '@/utils/logger'

/** How wide a fresh box is drawn. Its height is the one seeded line. */
const EDITOR_TEXT_BOX_SEED_WIDTH_PX = 200

/** The gap a fresh box keeps from whichever edge binds its top-left corner. */
const EDITOR_TEXT_BOX_SEED_INSET_PX = 15

/**
 * Where a fresh box's top-left corner goes, in the page's content space: the
 * seed inset from the top-left, or from the margin when the margin reaches
 * further in than that. A new box is meant to land inside the margins, so a
 * person never has to move one off a bleed it was inserted onto — the flat
 * 15 px this replaced sat inside the left margin of every product that has
 * one, and inside the top margin of all but the shallowest.
 *
 * Margins are measured from the page edge and items from the content edge,
 * so the margin is carried into content space by the page's padding, the way
 * `checkBleedOverlap` carries an item the other way. A layout with no
 * margins, or one whose padding already clears them, keeps the seed inset.
 * Only the top-left corner is placed: a box wider than the room between the
 * side margins still reaches the right one, which is a resize, not a
 * placement.
 */
export const editorTextBoxSeedOrigin = (
  margins: {
    margin_top?: number | null
    margin_left?: number | null
  } | null,
  padding: { top?: number | null; left?: number | null } | null,
): { x: number; y: number } => {
  const px = (value: number | null | undefined): number =>
    typeof value === 'number' && Number.isFinite(value) ? value : 0
  const inside = (
    margin: number | null | undefined,
    pad: number | null | undefined,
  ): number =>
    Math.max(
      EDITOR_TEXT_BOX_SEED_INSET_PX,
      px(margin) - px(pad) + EDITOR_TEXT_BOX_SEED_INSET_PX,
    )
  return {
    x: inside(margins?.margin_left, padding?.left),
    y: inside(margins?.margin_top, padding?.top),
  }
}

const seedBlocks = (): EditorTextBoxInput['blocks'] => [
  {
    kind: 'paragraph',
    runs: [{ kind: 'text', characters: EDITOR_TEXT_BOX_SEED_TEXT }],
  },
]

/**
 * Where a fresh box goes on the page the canvas shows: the seed origin for
 * the layout's margins and that page's padding.
 */
const seedOriginOf = async (
  snapshot: Snapshot,
): Promise<{ x: number; y: number }> => {
  const pages = await snapshot.getPromise(editorPagesAtom)
  const selectedPage = await snapshot.getPromise(selectedPageAtom)
  return editorTextBoxSeedOrigin(
    await snapshot.getPromise(baseLayoutMarginsSelector),
    pages[selectedPage - 1]?.product?.padding ?? null,
  )
}

/** Reads the seed origin of the page the canvas shows, for an insert that builds its own box. */
export const useEditorTextBoxSeedOrigin = () =>
  useRecoilCallback(
    ({ snapshot }) =>
      (): Promise<{ x: number; y: number }> =>
        seedOriginOf(snapshot),
    [],
  )

/**
 * Writes a new Lotus box onto the page the canvas shows and selects it.
 * Given blocks, or nothing, it builds the toolbar's seed box around them;
 * given a whole box, such as a converted custom verse, it writes that box as
 * it is. Either box is saved at the seed height and raised to its text by
 * its first paint on the canvas.
 */
export const useEditorInsertTextBox = () => {
  const syncCurrentPageToEditorPages = useSyncCurrentPageToEditorPages()
  const handleError = useErrorHandler('useEditorInsertTextBox')
  const { t } = useTranslation()

  return useRecoilCallback(
    ({ snapshot, set }) =>
      async (
        input: EditorTextBoxInput['blocks'] | EditorTextBoxInput = seedBlocks(),
      ): Promise<string | null> => {
        const pageId = await snapshot.getPromise(canvasPageIdAtom)
        if (!pageId) {
          handleError(
            'The text box could not be added to this page',
            new Error('insert without a canvas page id'),
          )
          return null
        }
        // The lock is the item's, not the row's, and a new box sets none of
        // its own: it follows the page's lock setting.
        const box: EditorTextBoxInput = Array.isArray(input)
          ? {
              boxId: uuidv4(),
              ...(await seedOriginOf(snapshot)),
              width: EDITOR_TEXT_BOX_SEED_WIDTH_PX,
              height: EDITOR_TEXT_BOX_SEED_HEIGHT_PX,
              zIndex: zLayers.content,
              blocks: input,
            }
          : input
        const { boxId } = box
        try {
          await saveEditorPageContent(pageId, {
            boxes: [box],
            deleteBoxIds: [],
          })
          const { data: bundle } = await getEditorPageContent(pageId)
          await syncCurrentPageToEditorPages()
          // The row is saved at the seed height; the box's first paint on
          // the canvas raises the frame to its text, and only a
          // box named here is raised (`textBoxSeedFrameHeightPx`).
          set(
            editorTextBoxSeedIdsAtom,
            (prev): Record<string, true> => ({ ...prev, [boxId]: true }),
          )
          set(editorPageContentAtomFamily(pageId), bundle)
          set(selectedItemsAtom, [boxId])
          set(unsavedChangesAtom, true)
        } catch (error) {
          // The save refuses a new box at a funeral home the Lotus limit does
          // not list; the editor loaded before that changed, so a reload
          // brings its inserts back in line.
          const refused =
            (error as { response?: { data?: { code?: unknown } } })?.response
              ?.data?.code === LOTUS_FUNERAL_HOME_NOT_ENABLED_CODE
          handleError(
            refused
              ? t('editor.textBox.insert.funeralHomeNotEnabled')
              : t('editor.textBox.insert.failed'),
            error instanceof Error ? error : new Error(String(error)),
          )
          return null
        }
        logger.info({
          message: 'editor_insert_text_box_success',
          category: EventCategory.INSERT,
          data: { pageId, boxId },
        })
        return boxId
      },
    [syncCurrentPageToEditorPages, handleError, t],
  )
}

export default useEditorInsertTextBox
